import { MaskLines, Reveal } from "../components/anim";
import { Eyebrow, CTAButton } from "../components/ui-bits";
import { COCKTAILS, TAP_EXTRA, STEPS } from "../data/site";
import useSEO from "../hooks/useSEO";

export default function CocktailServizi() {
  useSEO(
    "Cocktail e servizi: Spritz, Campari, Gin Tonic e analcolici | Sprizziamoci",
    "La selezione di cocktail e bevande alla spina di Sprizziamoci per il tuo evento, più il servizio in quattro passi: raccontaci l'evento, personalizziamo la proposta, prepariamo la postazione, si apre il servizio."
  );
  return (
    <article data-testid="cocktail-servizi-page">
      <section className="pt-32 md:pt-44 pb-14">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Eyebrow>Cocktail e servizi</Eyebrow>
          <MaskLines
            lines={["SCEGLI IL TUO", "PROSSIMO BRINDISI."]}
            as="h1"
            when="load"
            delay={0.15}
            lineClass="font-display font-semibold tracking-tight leading-[1] text-[clamp(2.6rem,6.5vw,5.5rem)]"
            className="mt-6"
          />
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-white/65">
              Cocktail pre-mix, birra, vino e prosecco, analcolici: tutto alla spina. Il menu si
              concorda sull'evento.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {COCKTAILS.map((c, i) => (
              <Reveal key={c.name} delay={i * 0.06}>
                <div className="group relative overflow-hidden rounded-2xl border border-white/[0.06] aspect-[3/4]">
                  <img
                    src={c.image}
                    alt={c.alt}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.07]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/95 via-brand-ink/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <p className="font-display text-xl md:text-2xl font-semibold">{c.name}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="flex h-full min-h-[220px] flex-col justify-between rounded-2xl border border-brand-orange/30 bg-brand-orange/[0.05] p-6">
                <p className="eyebrow">In più</p>
                <div>
                  <p className="font-display text-xl font-semibold leading-snug">
                    Altre referenze disponibili in base alla configurazione dell'impianto.
                  </p>
                  <p className="mt-3 text-xs leading-relaxed text-white/55">
                    Le referenze mostrate sono coerenti con l'effettiva disponibilità dei prodotti
                    e con le autorizzazioni applicabili.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {[TAP_EXTRA.birra, TAP_EXTRA.vino, TAP_EXTRA.analcolici].map((t, i) => (
              <Reveal key={t.title} delay={i * 0.08}>
                <div className="group relative overflow-hidden rounded-2xl border border-white/[0.06] h-56 md:h-64">
                  <img
                    src={t.image}
                    alt={t.alt}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-brand-ink/95 via-brand-ink/55 to-transparent" />
                  <div className="relative h-full flex flex-col justify-center p-8 max-w-sm">
                    <p className="eyebrow mb-2">Alla spina</p>
                    <p className="font-display text-2xl md:text-3xl font-semibold">{t.title}</p>
                    <p className="mt-2 text-sm leading-relaxed text-white/65">{t.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-10 rounded-2xl border border-white/[0.08] bg-brand-surface/40 p-6 md:p-8">
              <p className="text-sm leading-relaxed text-white/60">
                <span className="font-semibold text-brand-orange">Nota:</span> il menu definitivo
                viene concordato in base all'evento e alle bevande selezionate.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-brand-coal border-y border-white/[0.06]">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Eyebrow>Come funziona</Eyebrow>
          <MaskLines
            lines={["TU SCEGLI L'OCCASIONE.", "NOI COSTRUIAMO IL SERVIZIO."]}
            when="view"
            lineClass="font-display font-semibold tracking-tight leading-[1] text-[clamp(2.2rem,5vw,4.4rem)]"
            className="mt-6"
          />
          <div className="mt-16 grid gap-10 md:grid-cols-4 md:gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="relative border-t-2 border-brand-orange/30 pt-6">
                  <span className="font-display italic text-brand-orange text-5xl leading-none">
                    {s.n}
                  </span>
                  <h3 className="mt-4 font-display text-xl md:text-2xl font-semibold uppercase tracking-wide leading-snug">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/55">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-14 rounded-2xl border border-brand-orange/30 bg-brand-orange/[0.05] p-6 md:p-8 max-w-3xl">
              <p className="text-sm leading-relaxed text-white/70">
                <span className="font-semibold text-brand-orange">Self service:</span> i tuoi
                ospiti si servono da soli. Spina, ghiaccio e bicchieri sempre pronti.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-5 md:px-8 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <Reveal>
            <p className="font-display text-3xl md:text-5xl font-semibold leading-tight max-w-xl">
              Il prossimo brindisi <span className="italic text-brand-orange">è tuo.</span>
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <CTAButton to="/contatti" testId="cocktail-page-cta">
              Richiedi un preventivo
            </CTAButton>
          </Reveal>
        </div>
      </section>
</article>
  );
}
